import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { trpc, type RouterOutputs } from "@/lib/trpc";
import { useCurrencyFormatter } from "@/hooks/useCurrencyFormatter";
import { useRole } from "@/hooks/use-role";
import { NoteLine } from "@/components/shared/NoteLine";
import type { ErrorTableColumn } from "../bulk-stock-upload/ErrorTable";
import ChangesRowShell from "./ChangesRowShell";

/**
 * The field changes of a Base garments CSV upload in Logs > Bulk imports: one
 * line per SKU created and per value changed (a code's settings, a SKU's
 * weight, cost or image), with the value before and after. The stock lines sit
 * above it (BulkImportsTab), so stock is not repeated here. The states and the
 * table are ChangesRowShell's, as in SkuEditChangesRow and ClientPriceChangesRow.
 *
 * Costs and suppliers are cost data: without `skus.costs_manage` the line is
 * listed but its values are not shown.
 */

type Change = RouterOutputs["logs"]["getBaseGarmentChanges"][number];
type Field = Change["field"];

const FIELD_LABEL: Record<Field, string> = {
  sku_created: "baseGarments.logs.field.skuCreated",
  name: "baseGarments.identity.name",
  audience: "baseGarments.identity.whoCanUse",
  client_organization_id: "baseGarments.logs.field.client",
  default_print_method: "baseGarments.settings.printMethod",
  low_stock_alert: "baseGarments.settings.lowStockAlert",
  supplier_id: "baseGarments.settings.supplier",
  hs_code: "baseGarments.logs.field.hsCode",
  weight: "baseGarments.grid.weight",
  cost: "baseGarments.grid.cost",
  image_url: "baseGarments.grid.image",
};

const COST_FIELDS: ReadonlySet<Field> = new Set(["cost", "supplier_id"]);

export interface BaseGarmentChangesRowProps {
  batchId: string;
  /** Names an org id (own org, a connected client), as the Logs table does. */
  orgLabel?: (id: string) => string;
}

const BaseGarmentChangesRow = ({ batchId, orgLabel }: BaseGarmentChangesRowProps) => {
  const { t } = useTranslation("inventory");
  const { can } = useRole();
  const { formatWithCode } = useCurrencyFormatter();
  const canSeeChanges = can("skus.manage");
  const canSeeCosts = can("skus.costs_manage");

  // An applied upload never changes, so the list is fetched once per batch.
  const changesQuery = trpc.logs.getBaseGarmentChanges.useQuery(
    { batchId },
    { staleTime: Infinity, enabled: canSeeChanges }
  );

  const columns = useMemo<ErrorTableColumn<Change>[]>(() => {
    const show = (change: Change, side: "from" | "to"): string => {
      const value = change[side];
      if (change.field === "sku_created") return "";
      if (COST_FIELDS.has(change.field) && !canSeeCosts) return t("baseGarments.logs.value.costHidden");
      if (value === null || value === "") return t("baseGarments.logs.value.notSet");
      switch (change.field) {
        case "audience":
          return value === "all_clients"
            ? t("baseGarments.who.allClients")
            : value === "client"
              ? t("baseGarments.who.reservedFor")
              : t("baseGarments.who.onlyUs");
        case "client_organization_id":
          return typeof value === "string" ? (orgLabel ? orgLabel(value) : value.slice(0, 8)) : "";
        case "supplier_id":
          // The change carries the supplier's id only; no extra read to name it.
          return t("baseGarments.logs.value.supplierSet");
        case "cost":
          return typeof value === "number" || typeof value === "string"
            ? formatWithCode(value, side === "from" ? change.fromCurrency : change.toCurrency)
            : "";
        case "weight":
          if (typeof value === "object" && value !== null) {
            return value.value === null ? t("baseGarments.logs.value.notSet") : `${value.value} ${value.unit ?? "g"}`;
          }
          return String(value);
        default:
          return String(value);
      }
    };
    const field = (change: Change) => t(FIELD_LABEL[change.field]);
    const subject = (change: Change) => change.sku ?? change.code;
    return [
      {
        key: "sku",
        header: t("baseGarments.logs.col.sku"),
        className: "w-[28%]",
        cellClassName: "break-all font-mono",
        cell: subject,
        csv: subject,
        search: subject,
      },
      {
        key: "field",
        header: t("baseGarments.logs.col.field"),
        className: "w-[22%]",
        cellClassName: "[overflow-wrap:anywhere]",
        cell: field,
        csv: field,
        search: field,
      },
      {
        key: "from",
        header: t("baseGarments.logs.col.from"),
        className: "w-[25%]",
        cellClassName: "break-all tabular-nums text-muted-foreground",
        cell: (change) => show(change, "from") || "-",
        csv: (change) => show(change, "from"),
      },
      {
        key: "to",
        header: t("baseGarments.logs.col.to"),
        className: "w-[25%]",
        cellClassName: "break-all tabular-nums",
        cell: (change) => show(change, "to") || "-",
        csv: (change) => show(change, "to"),
      },
    ];
  }, [t, canSeeCosts, formatWithCode, orgLabel]);

  if (!canSeeChanges) {
    return <NoteLine as="p">{t("baseGarments.logs.needsSkusManage")}</NoteLine>;
  }

  return (
    <ChangesRowShell<Change>
      query={changesQuery}
      columns={columns}
      rowKey={(change, index) => `${change.skuId ?? change.code}:${change.field}:${index}`}
      filename={`base-garment-changes-${batchId}.csv`}
      searchPlaceholder={t("baseGarments.logs.search")}
      texts={{
        error: t("baseGarments.logs.error"),
        retry: t("baseGarments.view.retry"),
        empty: t("baseGarments.logs.noChanges"),
        more: ({ hidden }) => t("baseGarments.andMore", { count: hidden }),
      }}
    />
  );
};

export default BaseGarmentChangesRow;
